import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { VehicleListItemDto } from "../models/transport/vehicle";
import { EmployeeListItemDto } from "../models/employee/employee";
import { SalaryRowTypeItem } from "../models/salary/salaryRowTypeItem";
import { WorkTimeTypeListItem } from "../models/workTimeType/workTimeTypeListItem";
import { ICostCenterItem } from "../models/costCenter/costCenterItem";
import { Base } from "../framework/base";
import { getVehicles } from "../services/vehicleService";
import { getEmployees } from "../services/employeeServiceNew";
import { getSalaryRowTypeItems } from "../services/salaryRowTypeService";
import { getCostCenterItems } from "../services/costCenterService";
import { getAllWorkTimeTypes } from "../services/workTimeTypeService";
import { ICustomerItem } from "../models/customer/customerItem";
import { getCustomerItems } from "../services/customerService";
import { FuelType } from "../models/vehicle/fuelType";
import { employeeGroupListItemDto } from "../models/employeeGroup/employeeGroup";
import { getEmployeeGroups } from "../services/employeeGroupService";
import { SalaryPeriodTypeDto } from "../models/salaryPeriodType/salaryPeriodType";
import { getSalaryPeriodTypesWithPeriods } from "../services/salaryPeriodService";
import { t } from "i18next";
import { AutocompleteOption } from "../components/framework/muiAutocomplete";
import { GeofenceDto } from "../models/geofence/geofence";
import { getGeofences } from "../services/geofenceService";

export interface Filters {
    timeRange: [string, string];
    selectedVehicleGroups: string[];
    selectedVehicles: string[];
    selectedCostCenters: string[];
    selectedEmployees: string[];
    selectedEmployeeGroups: string[];
    selectedCustomers: string[];
    selectedWorkTimeTypes: string[];
}

function getReportOptionState<T>(
    items: T[],
    id: keyof T,
    name: keyof T
): ReportOptionState<T> {
    return {
        items,
        itemById: Base.arrayToRecord(items, id),
        itemsByName: Base.groupArray(items, name as string),
    };
}

export interface ReportOptionState<T> {
    items: T[];
    itemById: Record<string, T>;
    itemsByName: Record<string, T[]>;
}

export interface FuelEmission {
    fuelType: FuelType;
    emissionFactor: number;
}

export enum GroupingValue {
    Date = "dateStr",
    Employee = "employeeId",
    Vehicle = "vehicleId",
    Customer = "customerId",
    CostCenter = "costCenterId",
    WorkTimeType = "workTimeTypeId",
    WorkShift = "workShiftId",
    Geofence = "geofenceId",
}

export interface ReportOptionsState {
    vehicle: ReportOptionState<VehicleListItemDto>;
    employees: EmployeeListItemDto[];
    employeeGroups: employeeGroupListItemDto[];
    salaryRowTypes: SalaryRowTypeItem[];
    costCenters: ICostCenterItem[];
    costCenterOptions: AutocompleteOption[];
    customer: ReportOptionState<ICustomerItem>;
    workTimeType: ReportOptionState<WorkTimeTypeListItem>;
    salaryPeriodTypes: SalaryPeriodTypeDto[];
    geofences: GeofenceDto[];
    vehicleFilters: Filters;
    vehicleGrouping: string[];
    fuelEmissions: FuelEmission[];
}

const reportOptionDefaultState = {
    items: null,
    itemById: null,
    itemsByName: null,
};

export const initialState: ReportOptionsState = {
    vehicle: { ...reportOptionDefaultState },
    employees: null,
    employeeGroups: null,
    salaryRowTypes: null,
    costCenters: null,
    costCenterOptions: null,
    customer: { ...reportOptionDefaultState },
    workTimeType: { ...reportOptionDefaultState },
    salaryPeriodTypes: null,
    geofences: null,
    vehicleFilters: {
        timeRange: [
            Base.dayjsToJsonDate(
                Base.dateStartOf(Base.dateAddMonths(new Date(), -1), "month")
            ),
            Base.dayjsToJsonDate(
                Base.dateEndOf(Base.dateAddMonths(new Date(), -1), "month")
            ),
        ],
        selectedVehicleGroups: [],
        selectedVehicles: [],
        selectedCostCenters: [],
        selectedEmployees: [],
        selectedEmployeeGroups: [],
        selectedCustomers: [],
        selectedWorkTimeTypes: [],
    },
    vehicleGrouping: [GroupingValue.Vehicle],
    fuelEmissions: null,
};

export const reportOptionsSlice = createSlice({
    name: "reportOptions",
    initialState,
    reducers: {
        setVehicles: (state, action: PayloadAction<VehicleListItemDto[]>) => {
            state.vehicle = getReportOptionState(
                [
                    ...action.payload,
                    {
                        id: null,
                        registerNumber: t("reporting.noFleet"),
                    } as VehicleListItemDto,
                ],
                "id",
                "registerNumber"
            );
        },
        setEmployees: (state, action: PayloadAction<EmployeeListItemDto[]>) => {
            state.employees = action.payload;
        },
        setEmployeeGroups: (
            state,
            action: PayloadAction<employeeGroupListItemDto[]>
        ) => {
            state.employeeGroups = action.payload;
        },
        setSalaryRowTypes: (
            state,
            action: PayloadAction<SalaryRowTypeItem[]>
        ) => {
            state.salaryRowTypes = action.payload;
        },
        setCostCenters: (state, action: PayloadAction<ICostCenterItem[]>) => {
            state.costCenters = action.payload;
            state.costCenterOptions = [
                ...action.payload.map(({ id, name, customerName }) => ({
                    id,
                    title: name,
                    group: customerName,
                })),
                // Add no Cost center option
                {
                    id: null,
                    title: t("reporting.noCostCenter"),
                    group: "",
                } as AutocompleteOption,
            ] // Sort first so internal cost centers are first.
                .sort((a, b) => Base.stringCompare(a.group, b.group))
                .map((c) => ({
                    ...c,
                    // Add label for internal cost centers after sorting.
                    group: c.group || t("costCenter.internal"),
                }));
        },
        setCustomers: (state, action: PayloadAction<ICustomerItem[]>) => {
            state.customer = getReportOptionState(
                [
                    ...action.payload,
                    {
                        id: null,
                        name: t("reporting.noCustomer"),
                    } as ICustomerItem,
                ],
                "id",
                "name"
            );
        },
        setWorkTimeTypes: (
            state,
            action: PayloadAction<WorkTimeTypeListItem[]>
        ) => {
            state.workTimeType = getReportOptionState(
                [
                    ...action.payload,
                    {
                        workTimeTypeId: null,
                        name: t("reporting.noWorkTimeType"),
                    } as WorkTimeTypeListItem,
                ],
                "workTimeTypeId",
                "name"
            );
        },
        setSalaryPeriodTypes: (
            state,
            action: PayloadAction<SalaryPeriodTypeDto[]>
        ) => {
            state.salaryPeriodTypes = action.payload;
        },
        setGeofences: (state, action: PayloadAction<GeofenceDto[]>) => {
            state.geofences = action.payload;
        },
        setVehicleReportTimeRange(
            state,
            action: PayloadAction<[string, string]>
        ) {
            state.vehicleFilters.timeRange = action.payload;
        },
        setVehicleReportSelectedVehicleGroups(
            state,
            action: PayloadAction<string[]>
        ) {
            state.vehicleFilters.selectedVehicleGroups = action.payload;
        },
        setVehicleReportSelectedVehicles(
            state,
            action: PayloadAction<string[]>
        ) {
            state.vehicleFilters.selectedVehicles = action.payload;
        },
        setVehicleReportSelectedCostCenters(
            state,
            action: PayloadAction<string[]>
        ) {
            state.vehicleFilters.selectedCostCenters = action.payload;
        },
        setVehicleReportSelectedEmployees(
            state,
            action: PayloadAction<string[]>
        ) {
            state.vehicleFilters.selectedEmployees = action.payload;
        },
        setVehicleReportSelectedEmployeeGroups(
            state,
            action: PayloadAction<string[]>
        ) {
            state.vehicleFilters.selectedEmployeeGroups = action.payload;
        },
        setVehicleReportSelectedCustomers(
            state,
            action: PayloadAction<string[]>
        ) {
            state.vehicleFilters.selectedCustomers = action.payload;
        },
        setVehicleReportSelectedWorkTimeTypes(
            state,
            action: PayloadAction<string[]>
        ) {
            state.vehicleFilters.selectedWorkTimeTypes = action.payload;
        },
        clearVehilceReportFilters: (state) => {
            state.vehicleFilters = { ...initialState.vehicleFilters };
        },
        setVehicleReportGrouping: (state, action: PayloadAction<string[]>) => {
            state.vehicleGrouping = action.payload.filter(Boolean);
        },
    },
});

export const {
    setVehicles,
    setEmployees,
    setEmployeeGroups,
    setSalaryRowTypes,
    setCostCenters,
    setCustomers,
    setWorkTimeTypes,
    setSalaryPeriodTypes,
    setGeofences,
    setVehicleReportTimeRange,
    setVehicleReportSelectedVehicleGroups,
    setVehicleReportSelectedVehicles,
    setVehicleReportSelectedCostCenters,
    setVehicleReportSelectedEmployees,
    setVehicleReportSelectedEmployeeGroups,
    setVehicleReportSelectedCustomers,
    setVehicleReportSelectedWorkTimeTypes,
    clearVehilceReportFilters,
    setVehicleReportGrouping,
} = reportOptionsSlice.actions;

export const fetchReportVehicles = createAsyncThunk(
    "reportOptions/vehicles",
    async (_, thunkApi) => {
        const vehicles = await getVehicles(true, thunkApi.signal);
        thunkApi.dispatch(setVehicles(vehicles?.data));
    }
);

export const fetchReportEmployees = createAsyncThunk(
    "reportOptions/employees",
    async (_, thunkApi) => {
        const employees = await getEmployees(null, thunkApi.signal);
        thunkApi.dispatch(setEmployees(employees?.data));
    }
);

export const fetchReportEmployeeGroups = createAsyncThunk(
    "reportOptions/employeeGroups",
    async (_, thunkApi) => {
        const employeeGroups = await getEmployeeGroups(true, thunkApi.signal);
        thunkApi.dispatch(setEmployeeGroups(employeeGroups?.data));
    }
);

export const fetchReportSalaryRowTypes = createAsyncThunk(
    "reportOptions/salaryRowTypes",
    async (_, thunkApi) => {
        const salaryRowTypes = await getSalaryRowTypeItems(
            1000,
            1,
            "",
            "name",
            true
        );
        thunkApi.dispatch(
            setSalaryRowTypes(
                salaryRowTypes?.items.sort((a, b) =>
                    Base.stringCompare(a.code, b.code)
                )
            )
        );
    }
);

export const fetchReportCostCenters = createAsyncThunk(
    "reportOptions/costCenters",
    async (_, thunkApi) => {
        // NOTE: Using getCostCenterItems for getting available costCenter names in report is not good way as it uses paging and can limit results
        // if results are limited report cannot find the Cost center name for costCenterId and shows "No customer"
        const costCenters = await getCostCenterItems(
            10000, // Lifted from 1000 to 10 thousand, but still limit can be reached
            1,
            "",
            "name",
            true,
            true
        );
        thunkApi.dispatch(setCostCenters(costCenters?.items));
    }
);

export const fetchReportCustomers = createAsyncThunk(
    "reportOptions/customers",
    async (_, thunkApi) => {
        // NOTE: Using getCustomerItems for getting available customer names in report is not good way as it uses paging and can limit results
        // if results are limited report cannot find the Customer name for customerId and shows "No customer"
        const customers = await getCustomerItems(
            10000, // Lifted from 1000 to 10 thousand, but still limit can be reached
            1,
            "",
            "name",
            true,
            true
        );

        thunkApi.dispatch(setCustomers(customers?.items));
    }
);

export const fetchReportWorkTimeTypes = createAsyncThunk(
    "reportOptions/workTimeTypes",
    async (_, thunkApi) => {
        const workTimeTypes = await getAllWorkTimeTypes(thunkApi.signal);
        thunkApi.dispatch(setWorkTimeTypes(workTimeTypes));
    }
);

export const fetchReportSalaryPeriodTypes = createAsyncThunk(
    "reportOptions/salaryPeriodTypes",
    async (_, thunkApi) => {
        const salaryPeriodTypes = await getSalaryPeriodTypesWithPeriods(
            thunkApi.signal
        );
        thunkApi.dispatch(setSalaryPeriodTypes(salaryPeriodTypes));
    }
);

export const fetchReportGeofences = createAsyncThunk(
    "reportOptions/geofences",
    async (_, thunkApi) => {
        const geofences = await getGeofences(undefined, undefined, thunkApi.signal);
        thunkApi.dispatch(setGeofences(geofences?.data));
    }
);

export const reportOptionsReducer = reportOptionsSlice.reducer;
